
/**
 * Storage Quota Manager
 * 
 * Monitors and manages local storage usage to prevent quota errors
 */

import { enterRecoveryMode, isInRecoveryMode } from './recoveryUtils';

// Estimated quota for localStorage (browsers typically allow 5-10MB)
const ESTIMATED_QUOTA_BYTES = 5 * 1024 * 1024; // 5MB is conservative estimate
const CRITICAL_THRESHOLD = 0.9; // 90% of capacity

/**
 * Get the current storage usage
 */
export const getStorageUsage = (): { usage: number; percentage: number; remaining: number } => {
  try {
    let totalBytes = 0;
    
    // Calculate total bytes used in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      const value = localStorage.getItem(key) || '';
      // Each character is 2 bytes in UTF-16, plus key storage
      totalBytes += (key.length + value.length) * 2;
    }
    
    const percentage = totalBytes / ESTIMATED_QUOTA_BYTES;
    const remaining = ESTIMATED_QUOTA_BYTES - totalBytes;
    
    return {
      usage: totalBytes,
      percentage,
      remaining
    };
  } catch (e) {
    // If we can't calculate storage, return conservative estimate
    return {
      usage: 0,
      percentage: 0,
      remaining: ESTIMATED_QUOTA_BYTES
    };
  }
};

/**
 * Check if storage is close to quota
 */
export const isStorageNearQuota = (): boolean => {
  try {
    const { percentage } = getStorageUsage();
    return percentage > CRITICAL_THRESHOLD;
  } catch (e) {
    return false;
  }
};

/**
 * Check if we can safely store a value of the given size
 */
export const canSafelyStore = (valueSize: number): boolean => {
  try {
    const { remaining } = getStorageUsage();
    // Add 20% margin for safety
    return remaining > valueSize * 1.2;
  } catch (e) {
    return true; // Assume we can, and let the actual operation fail if needed
  }
};

/**
 * Detect and handle storage quota issues
 * @returns boolean True if there are quota issues
 */
export const handleStorageQuotaIssues = (): boolean => {
  try {
    // Skip if in recovery mode
    if (isInRecoveryMode()) return true;
    
    // Check if localStorage is close to being full by trying to add a test item
    const testKey = `quota-test-${Date.now()}`;
    const testValue = new Array(1000).join('x'); // ~1KB test value
    
    try {
      localStorage.setItem(testKey, testValue);
      localStorage.removeItem(testKey);
      
      // If we get here, we have enough space
      // But still check actual usage percentage
      const { percentage } = getStorageUsage();
      if (percentage > CRITICAL_THRESHOLD) {
        console.warn(`⚠️ Storage usage high (${(percentage * 100).toFixed(1)}%), performing cleanup`);
        performEmergencyCleanup(0.3); // Remove 30% of tracking data
        return true;
      }
      
      return false;
    } catch (e) {
      console.warn('⚠️ Storage quota exceeded, entering recovery mode');
      
      // Emergency cleanup - very aggressive to recover space
      performEmergencyCleanup(0.75); // 75% removal
      
      // Enter recovery mode
      enterRecoveryMode();
      
      return true;
    }
  } catch (e) {
    console.error('❌ Failed to handle storage quota issues:', e);
    return false;
  }
};

/**
 * Perform emergency cleanup of storage
 * @param targetReduction Percentage of storage to free (0-1)
 */
export const performEmergencyCleanup = async (targetReduction: number = 0.5): Promise<number> => {
  try {
    const initialUsage = getStorageUsage();
    let removedCount = 0;
    
    // If usage is already low, no need for cleanup
    if (initialUsage.percentage < 0.5) {
      return 0;
    }
    
    // Get all keys and their sizes
    const items: Array<{ key: string; size: number; isPriority: boolean }> = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      const value = localStorage.getItem(key) || '';
      const size = (key.length + value.length) * 2; // UTF-16 character size
      
      // Determine if this is a priority item that should be preserved
      const isPriority = 
        key.includes('auth') || 
        key.includes('user') || 
        key.includes('token') ||
        key.includes('session') ||
        key.includes('settings') ||
        key.includes('subscription');
      
      items.push({ key, size, isPriority });
    }
    
    // Sort by size (largest first) and priority (non-priority first)
    items.sort((a, b) => {
      if (a.isPriority !== b.isPriority) {
        return a.isPriority ? 1 : -1; // Non-priority items first
      }
      return b.size - a.size; // Larger items first
    });
    
    // Calculate target bytes to remove
    const bytesToRemove = initialUsage.usage * targetReduction;
    let bytesRemoved = 0;
    
    // Remove items until we hit our target
    for (const item of items) {
      if (bytesRemoved >= bytesToRemove && removedCount > 0) {
        break;
      }
      
      // Skip priority items unless we're desperate
      if (item.isPriority && bytesRemoved < bytesToRemove * 0.7) {
        continue;
      }
      
      try {
        localStorage.removeItem(item.key);
        bytesRemoved += item.size;
        removedCount++;
      } catch (e) {
        // Continue with other items
      }
    }
    
    // Clear all of sessionStorage as well (it's temporary anyway)
    const sessionLength = sessionStorage.length;
    sessionStorage.clear();
    removedCount += sessionLength;
    
    // Check how much we freed up
    const finalUsage = getStorageUsage();
    const freedPercentage = initialUsage.percentage - finalUsage.percentage;
    
    console.log(`Storage cleanup: removed ${removedCount} items, freed ${(freedPercentage * 100).toFixed(1)}% of storage`);
    
    return removedCount;
  } catch (e) {
    console.error('Storage emergency cleanup failed:', e);
    return 0;
  }
};

export default {
  getStorageUsage,
  isStorageNearQuota,
  canSafelyStore,
  performEmergencyCleanup,
  handleStorageQuotaIssues
};
